/** A portable calculation proposal, never an observed scientific outcome. The
 * recipe is allowlisted and contains numbers, not source code to evaluate. */
import { diagnoseSharedOffset } from './sharedOffsetDiagnostic'
import type { SharedOffsetDiagnostic, SharedOffsetInput } from './sharedOffsetDiagnostic'

export const COMPUTATIONAL_ARTIFACT_KIND = 'pathways-computational-artifact' as const
export const COMPUTATIONAL_ARTIFACT_VERSION = 1 as const
export const COMPUTATIONAL_RECIPE = 'shared-offset-hard-bounds-v1' as const
export const COMPUTATIONAL_IMPLEMENTATION =
  'pathways-common-offset-interval-intersection-v1' as const
export const COMPUTATIONAL_REPRODUCE_COMMAND =
  'node scripts/reproduce-computational-artifact.mjs <artifact.json>'
export const COMPUTATIONAL_LIMITS = {
  maxInputBytes: 262144,
  maxObservations: 256,
  maxModels: 16,
  maxReferences: 32,
  maxStringLength: 2000,
  network: 'none',
  arbitraryCode: 'disallowed',
} as const

export interface ComputationalArtifactOptions {
  id: string
  title: string
  createdAt: number
  inputs: SharedOffsetInput
  provenance: {
    origin: 'authored-example' | 'user-supplied' | 'model-proposed'
    inputStatus: 'synthetic' | 'reported-observations' | 'hypothetical'
    description: string
    references: string[]
  }
  context: {
    sessionId: string | null
    nodeId: string | null
    testId: string | null
    claimAtCreation: string | null
  }
  environment: {
    runtime: 'browser' | 'node' | 'unknown'
    version: string | null
    platform: string | null
  }
  /** A supplied build identity, or explicitly unknown; never guessed from a model. */
  sourceCommit: string | null
}

type AssessedDiagnostic = Exclude<SharedOffsetDiagnostic, { kind: 'not-assessed' }>
export interface ComputationalArtifact {
  kind: typeof COMPUTATIONAL_ARTIFACT_KIND
  version: typeof COMPUTATIONAL_ARTIFACT_VERSION
  id: string
  title: string
  createdAt: number
  recipe: typeof COMPUTATIONAL_RECIPE
  inputs: SharedOffsetInput
  provenance: ComputationalArtifactOptions['provenance']
  context: ComputationalArtifactOptions['context']
  implementation: {
    id: typeof COMPUTATIONAL_IMPLEMENTATION
    arithmetic: 'finite-ieee754-inclusive-no-tolerance'
    sourceCommit: string | null
    createdIn: ComputationalArtifactOptions['environment']
  }
  limits: typeof COMPUTATIONAL_LIMITS
  expectedOutput: AssessedDiagnostic
  semantics: {
    kind: 'conditional-numeric-calculation'
    scientificValidation: false
    sourceAdmission: false
    observedOutcome: false
    expectations: 'computed-by-declared-implementation-not-independent-reference'
  }
}

const SEMANTICS: ComputationalArtifact['semantics'] = {
  kind: 'conditional-numeric-calculation',
  scientificValidation: false,
  sourceAdmission: false,
  observedOutcome: false,
  expectations: 'computed-by-declared-implementation-not-independent-reference',
}
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/** Canonical data equality rejects unrepresentable values and avoids accepting
 * functions/undefined merely because JSON.stringify would silently drop them. */
function canonical(value: unknown, depth = 0, budget = { bytes: 0 }): string {
  if (depth > 20) throw new Error('The artifact is nested too deeply.')
  const token = (text: string) => {
    budget.bytes += new TextEncoder().encode(text).byteLength
    if (budget.bytes > COMPUTATIONAL_LIMITS.maxInputBytes)
      throw new Error('The artifact exceeds the input-byte limit.')
    return text
  }
  if (typeof value === 'string' && value.length > COMPUTATIONAL_LIMITS.maxStringLength)
    throw new Error('A string exceeds the artifact string-length limit.')
  if (value === null || typeof value === 'boolean' || typeof value === 'string')
    return token(JSON.stringify(value))
  if (typeof value === 'number' && Number.isFinite(value)) return token(JSON.stringify(value))
  if (Array.isArray(value)) {
    if (
      Object.getOwnPropertyNames(value).length !== value.length + 1 ||
      Object.getOwnPropertySymbols(value).length
    )
      throw new Error('Invalid array properties.')
    for (const descriptor of Object.values(Object.getOwnPropertyDescriptors(value)))
      if (descriptor.get || descriptor.set) throw new Error('Accessor properties are not data.')
    if (value.length > COMPUTATIONAL_LIMITS.maxInputBytes)
      throw new Error('The artifact exceeds the input-byte limit.')
    if (Object.keys(value).length !== value.length) throw new Error('Invalid array properties.')
    const entries = []
    for (let i = 0; i < value.length; i++) entries.push(canonical(value[i], depth + 1, budget))
    token('[]' + ','.repeat(Math.max(0, entries.length - 1)))
    return `[${entries.join(',')}]`
  }
  if (object(value)) {
    if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)
      throw new Error('Only plain JSON objects are permitted.')
    if (Object.getOwnPropertySymbols(value).length)
      throw new Error('Symbol keys are not permitted.')
    for (const descriptor of Object.values(Object.getOwnPropertyDescriptors(value)))
      if (descriptor.get || descriptor.set || !descriptor.enumerable)
        throw new Error('Only enumerable data properties are permitted.')
    const keys = Object.keys(value).sort()
    const entries = keys.map(
      (key) => `${token(JSON.stringify(key) + ':')}${canonical(value[key], depth + 1, budget)}`,
    )
    token('{}' + ','.repeat(Math.max(0, entries.length - 1)))
    return `{${entries.join(',')}}`
  }
  throw new Error('The artifact contains a value that JSON cannot preserve.')
}

function metadataIssues(value: Record<string, unknown>): string[] {
  const issues: string[] = []
  const text = (item: unknown, name: string, nullable = false) => {
    if (nullable && item === null) return
    if (
      typeof item !== 'string' ||
      !item.trim() ||
      item.length > COMPUTATIONAL_LIMITS.maxStringLength
    )
      issues.push(`${name} must be a nonempty bounded string${nullable ? ' or null' : ''}.`)
  }
  text(value.id, 'id')
  text(value.title, 'title')
  if (
    typeof value.createdAt !== 'number' ||
    !Number.isSafeInteger(value.createdAt) ||
    value.createdAt < 0
  )
    issues.push('createdAt must be a finite nonnegative epoch-millisecond value.')
  const provenance = value.provenance
  if (!object(provenance)) issues.push('Provenance is required.')
  else {
    if (
      typeof provenance.origin !== 'string' ||
      !['authored-example', 'user-supplied', 'model-proposed'].includes(provenance.origin)
    )
      issues.push('Unknown provenance origin.')
    if (
      typeof provenance.inputStatus !== 'string' ||
      !['synthetic', 'reported-observations', 'hypothetical'].includes(provenance.inputStatus)
    )
      issues.push('Unknown input status.')
    if (provenance.origin === 'authored-example' && provenance.inputStatus !== 'synthetic')
      issues.push('Authored examples must identify their observations as synthetic.')
    text(provenance.description, 'provenance.description')
    if (
      !Array.isArray(provenance.references) ||
      provenance.references.length > COMPUTATIONAL_LIMITS.maxReferences
    )
      issues.push('Provenance references exceed the allowed shape or limit.')
    else for (const ref of provenance.references) text(ref, 'provenance.references[]')
  }
  if (!object(value.context)) issues.push('Context with explicit unknown fields is required.')
  else
    for (const key of ['sessionId', 'nodeId', 'testId', 'claimAtCreation'])
      text(value.context[key], `context.${key}`, true)
  const implementation = value.implementation
  if (!object(implementation)) issues.push('Implementation identity is required.')
  else {
    if (
      implementation.id !== COMPUTATIONAL_IMPLEMENTATION ||
      implementation.arithmetic !== 'finite-ieee754-inclusive-no-tolerance'
    )
      issues.push('Unsupported implementation or arithmetic version.')
    if (
      implementation.sourceCommit !== null &&
      (typeof implementation.sourceCommit !== 'string' ||
        !/^(?:[a-f0-9]{40}|[a-f0-9]{64})$/.test(implementation.sourceCommit))
    )
      issues.push('Source commit must be an exact hexadecimal commit id or null.')
    const environment = implementation.createdIn
    if (!object(environment)) issues.push('Creation environment is required.')
    else {
      if (
        typeof environment.runtime !== 'string' ||
        !['browser', 'node', 'unknown'].includes(environment.runtime)
      )
        issues.push('Unknown runtime kind.')
      text(environment.version, 'environment.version', true)
      text(environment.platform, 'environment.platform', true)
    }
  }
  return issues
}

export type ComputationalArtifactInspection =
  | { ok: true; artifact: ComputationalArtifact; result: AssessedDiagnostic; issues: [] }
  | { ok: false; issues: string[] }

/** Recomputing a saved expectation establishes numeric consistency, not who
 * authored the inputs or whether a measurement occurred. Nothing is recorded. */
export function inspectComputationalArtifact(value: unknown): ComputationalArtifactInspection {
  const issues: string[] = []
  if (!object(value)) return { ok: false, issues: ['Expected a computational artifact object.'] }
  try {
    if (new TextEncoder().encode(canonical(value)).byteLength > COMPUTATIONAL_LIMITS.maxInputBytes)
      return { ok: false, issues: ['The artifact exceeds the input-byte limit.'] }
    if (
      value.kind !== COMPUTATIONAL_ARTIFACT_KIND ||
      value.version !== COMPUTATIONAL_ARTIFACT_VERSION
    )
      issues.push('Unsupported artifact kind or version.')
    if (value.recipe !== COMPUTATIONAL_RECIPE)
      issues.push('Only the shared-offset recipe is permitted.')
    if (canonical(value.limits) !== canonical(COMPUTATIONAL_LIMITS))
      issues.push('Resource limits differ from the supported recipe.')
    if (canonical(value.semantics) !== canonical(SEMANTICS))
      issues.push('Outcome semantics differ from the supported recipe.')
    issues.push(...metadataIssues(value))
    const inputs = value.inputs
    if (!object(inputs)) issues.push('Numeric inputs are required.')
    else {
      if (
        !Array.isArray(inputs.observations) ||
        inputs.observations.length > COMPUTATIONAL_LIMITS.maxObservations
      )
        issues.push('Observation limit exceeded or observations missing.')
      if (!Array.isArray(inputs.models) || inputs.models.length > COMPUTATIONAL_LIMITS.maxModels)
        issues.push('Candidate limit exceeded or candidates missing.')
    }
    if (issues.length) return { ok: false, issues }
    const result = diagnoseSharedOffset(inputs)
    if (result.kind === 'not-assessed')
      return {
        ok: false,
        issues: result.issues.map((issue) => `${issue.path}: ${issue.reason}`),
      }
    if (canonical(result) !== canonical(value.expectedOutput))
      return { ok: false, issues: ['Expected output differs from the allowlisted calculation.'] }
    return {
      ok: true,
      artifact: structuredClone(value) as unknown as ComputationalArtifact,
      result,
      issues: [],
    }
  } catch (error) {
    return {
      ok: false,
      issues: [error instanceof Error ? error.message : 'Invalid artifact data.'],
    }
  }
}

export function createComputationalArtifact(
  options: ComputationalArtifactOptions,
): ComputationalArtifact {
  canonical(options)
  if (
    options.inputs.observations.length > COMPUTATIONAL_LIMITS.maxObservations ||
    options.inputs.models.length > COMPUTATIONAL_LIMITS.maxModels
  )
    throw new Error('Numeric input dimensions exceed the recipe limits.')
  const result = diagnoseSharedOffset(options.inputs)
  if (result.kind === 'not-assessed')
    throw new Error(result.issues.map((issue) => `${issue.path}: ${issue.reason}`).join('; '))
  const artifact: ComputationalArtifact = {
    kind: COMPUTATIONAL_ARTIFACT_KIND,
    version: COMPUTATIONAL_ARTIFACT_VERSION,
    id: options.id,
    title: options.title,
    createdAt: options.createdAt,
    recipe: COMPUTATIONAL_RECIPE,
    inputs: options.inputs,
    provenance: options.provenance,
    context: options.context,
    implementation: {
      id: COMPUTATIONAL_IMPLEMENTATION,
      arithmetic: 'finite-ieee754-inclusive-no-tolerance',
      sourceCommit: options.sourceCommit,
      createdIn: options.environment,
    },
    limits: COMPUTATIONAL_LIMITS,
    expectedOutput: result,
    semantics: SEMANTICS,
  }
  const inspection = inspectComputationalArtifact(artifact)
  if (!inspection.ok) throw new Error(inspection.issues.join('; '))
  return inspection.artifact
}

export function serializeComputationalArtifact(artifact: ComputationalArtifact): string {
  const inspection = inspectComputationalArtifact(artifact)
  if (!inspection.ok) throw new Error(inspection.issues.join('; '))
  const text = `${JSON.stringify(inspection.artifact, null, 2)}\n`
  if (new TextEncoder().encode(text).byteLength > COMPUTATIONAL_LIMITS.maxInputBytes)
    throw new Error('The serialized artifact exceeds the input-byte limit.')
  return text
}

/** Safe demonstration inputs, not records of experiments and not independent
 * benchmark answers. Linking an example to a proposed test does not change that. */
export const AUTHORED_COMPUTATIONAL_CASES = [
  {
    id: 'common-offset-compatible-linear',
    title: 'One compatible candidate under a common offset',
    description:
      'Authored synthetic endpoint readings. The linear candidate fits with offset 100 ms; the quadratic candidate does not.',
    inputs: {
      unit: 'ms',
      errorModel: 'hard-absolute-bound',
      observations: [100, 104, 114, 118],
      errorBounds: [2, 2, 2, 2],
      models: [
        { id: 'linear', predictions: [2, 2, 16, 16] },
        { id: 'quadratic', predictions: [1, 1, 64, 64] },
      ],
    },
  },
  {
    id: 'common-offset-inconclusive-counterexample',
    title: 'Mean contrast passes, but neither candidate fits',
    description:
      'Authored development counterexample identified after pilot-output inspection. Synthetic readings have mean contrast 18 ms, but no single offset makes the linear residuals fit the hard bounds.',
    inputs: {
      unit: 'ms',
      errorModel: 'hard-absolute-bound',
      observations: [0, 4, 20, 20],
      errorBounds: [2, 2, 2, 2],
      models: [
        { id: 'linear', predictions: [2, 2, 16, 16] },
        { id: 'quadratic', predictions: [1, 1, 64, 64] },
      ],
    },
  },
] satisfies { id: string; title: string; description: string; inputs: SharedOffsetInput }[]
